import { z } from "zod";

import { checkFormat, publicIdFormat, type FormatSpec } from "./format";
import {
  ID_KIND_META_KEY,
  agentRefFormat,
  externalIdFormat,
  opaqueIdFormat,
  polymorphicIdFormat,
  taskRefFormat,
  type ExternalIdKind,
  type OpaqueIdKind,
  type TaskRef,
} from "./id-formats";
import type { PublicId, PublicIdResourceType } from "./public-id";

/** Key on a zod issue's `params` that marks it as a {@link zFormat} rejection. */
export const FORMAT_ISSUE_PARAM = "quovyFormat";

export type MalformedFormatIssue = {
  /** Where the value sat in the parsed input. */
  path: PropertyKey[];
  /** The spec's noun, e.g. `"task id"`. */
  name: string;
  /** The rejected value, truncated for logs. */
  received: string;
};

function formatMessage(value: unknown, spec: FormatSpec<string>): string | undefined {
  if (value === undefined) return undefined;
  const result = checkFormat(value, spec);
  return result.ok ? undefined : result.error.message;
}

/**
 * Adds `spec`'s rejection of `value` to a refinement, marked so tool-input
 * handlers can log it. Returns whether the value passed.
 */
export function refineFormat(
  value: unknown,
  spec: FormatSpec<string>,
  ctx: z.RefinementCtx,
  opts: { optional?: boolean } = {},
): boolean {
  const result = checkFormat(value, spec, opts);
  if (result.ok) return true;
  ctx.addIssue({
    code: "custom",
    message: result.error.message,
    input: value,
    params: { [FORMAT_ISSUE_PARAM]: spec.name, received: result.error.received },
  });
  return false;
}

/** A zod string whose non-string rejection still names the form `spec` expects. */
export function zFormatString(spec: FormatSpec<string>): z.ZodString {
  return z.string({ error: (issue) => formatMessage(issue.input, spec) });
}

/** The JSON-schema keys (`pattern`, `x-id-kind`) a schema checked by `spec` carries. */
export function formatSchemaMeta(spec: FormatSpec<string>): Record<string, string> {
  return {
    ...(spec.pattern ? { pattern: spec.pattern.source } : {}),
    ...(spec.idKind ? { [ID_KIND_META_KEY]: spec.idKind } : {}),
  };
}

/**
 * A zod string checked by `spec`: a rejection carries the same what + how as
 * {@link FormatError}, and the spec's `pattern` is emitted in the JSON schema
 * so tool-calling models and OpenAPI readers see the form up front.
 */
export function zFormat<T extends string>(spec: FormatSpec<T>): z.ZodType<T, string> {
  return optionalAware(buildFormat(spec, false), () =>
    buildFormat(spec, true),
  ) as unknown as z.ZodType<T, string>;
}

function buildFormat(spec: FormatSpec<string>, optional: boolean): z.ZodType {
  const schema = zFormatString(spec).superRefine((value, ctx) => {
    refineFormat(value, spec, ctx, { optional });
  });
  // The refinement above is what proves the brand; zod cannot express it.
  return spec.pattern || spec.idKind ? schema.meta(formatSchemaMeta(spec)) : schema;
}

type Methods = Record<string, unknown>;
const CLONING_METHODS = ["describe", "meta", "openapi"] as const;

/**
 * deliberate-long-comment: the optionality mechanism is not visible from a call site.
 * The check inside a zod schema cannot see whether the field around it is
 * optional, yet a blank id's message must say "or omit" only when it is. So
 * an id helper returns its required form, and `.optional()` / `.nullish()` /
 * `.nullable().optional()` swap in the optional form. `.describe()`,
 * `.meta()` and `.openapi()` clone the schema (dropping these overrides), so
 * they are wrapped to carry the swap through, replaying themselves on the
 * optional form.
 */
function optionalAware(required: z.ZodType, optionalForm: () => z.ZodType): z.ZodType {
  const self = required as unknown as Methods;
  self.optional = () => z.optional(optionalForm());
  self.nullish = () => z.optional(z.nullable(optionalForm()));
  self.nullable = () => {
    const nullable = z.nullable(required) as unknown as Methods;
    nullable.optional = () => z.optional(z.nullable(optionalForm()));
    nullable.nullish = nullable.optional;
    return nullable;
  };
  for (const method of CLONING_METHODS) {
    const original = self[method];
    if (typeof original !== "function") continue;
    self[method] = (...args: unknown[]) => {
      const result = (original as (...a: unknown[]) => unknown).apply(self, args);
      // `.meta()` with no argument reads the metadata rather than cloning.
      if (args.length === 0 || !(result instanceof z.ZodType)) return result;
      return optionalAware(result, () => {
        const next = optionalForm() as unknown as Methods;
        return (next[method] as (...a: unknown[]) => z.ZodType).apply(next, args);
      });
    };
  }
  return required;
}

/**
 * A public id of one registered resource type (or any of several), parsed to
 * the branded {@link PublicId}. Use for every id an agent tool or API request
 * accepts; `howToResolve` should name where a valid id comes from.
 */
export function zPublicId<T extends PublicIdResourceType>(
  type: T | readonly T[],
  opts: { label?: string; howToResolve?: string } = {},
): z.ZodType<PublicId<T>, string> {
  return zFormat(publicIdFormat(type, opts));
}

/**
 * A bulk item's id, documented like `zFormat(spec)` but parsed leniently: the
 * handler checks each item with `checkFormat(value, spec)` so one malformed id
 * fails only its own item, not the batch.
 */
export function zBulkItemId(spec: FormatSpec<string>): z.ZodType<string, string> {
  return z.string().min(1).meta(formatSchemaMeta(spec));
}

/** `idSchema`'s anchored JSON-schema pattern without its anchors, for composing a wider one. */
export function idPatternBody(idSchema: z.ZodType): string | undefined {
  const pattern = (z.toJSONSchema(idSchema) as { pattern?: string }).pattern;
  return pattern?.replace(/^\^/, "").replace(/\$$/, "");
}

/** A task's public id (`tsk_…`) or number (`T-123`; agent tools also take `123`). */
export function zTaskRef(
  opts: { allowBareNumber?: boolean; howToResolve?: string } = {},
): z.ZodType<TaskRef, string> {
  return zFormat(taskRefFormat(opts));
}

/** An agent's public id (`agt_…`) or slug. */
export function zAgentRef(opts: { howToResolve?: string } = {}): z.ZodType<string, string> {
  return zFormat(agentRefFormat(opts));
}

/**
 * A public id whose type a sibling field selects. Pass the endpoint's own
 * allow-list (taggable, linkable, …), or `"any"` when the handler itself
 * answers an unsupported type.
 */
export function zPolymorphicId(
  types: readonly PublicIdResourceType[] | "any",
  opts: { label?: string; howToResolve?: string } = {},
): z.ZodType<string, string> {
  return zFormat(polymorphicIdFormat(types, opts));
}

/** A provider's id, checked against its kind in `EXTERNAL_ID_FORMATS`. */
export function zExternalId(kind: ExternalIdKind): z.ZodType<string, string> {
  return zFormat(externalIdFormat(kind));
}

/** A non-public internal id (UUID, queue job, definition key), by kind in `OPAQUE_ID_FORMATS`. */
export function zOpaqueId(kind: OpaqueIdKind): z.ZodType<string, string> {
  return zFormat(opaqueIdFormat(kind));
}

function issuesOf(error: unknown): readonly unknown[] | undefined {
  let current: unknown = error;
  for (let depth = 0; depth < 5 && current && typeof current === "object"; depth += 1) {
    const issues = (current as { issues?: unknown }).issues;
    if (Array.isArray(issues)) return issues;
    current = (current as { cause?: unknown }).cause;
  }
  return undefined;
}

/**
 * The {@link zFormat} rejections inside a validation failure, found on a
 * `ZodError` or through the `cause` chain an SDK wraps it in.
 */
export function malformedFormatIssues(error: unknown): MalformedFormatIssue[] {
  const out: MalformedFormatIssue[] = [];
  for (const issue of issuesOf(error) ?? []) {
    const { path, params } = issue as { path?: PropertyKey[]; params?: Record<string, unknown> };
    const name = params?.[FORMAT_ISSUE_PARAM];
    if (typeof name !== "string") continue;
    out.push({ path: path ?? [], name, received: String(params?.received ?? "") });
  }
  return out;
}
